import React, { useState, useEffect, useCallback, useRef } from "react"
import { Popover, Button, Stack, Checkbox, TextField } from "@shopify/polaris"
import { useTranslation } from "react-i18next"
import { useAppContext } from "components/AppContext"
import { useEntity } from "hooks/useAxios"
import NodeEntity from "services/entities/NodeEntity"
import NodeOrganization from "services/organizations/NodeOrganization"

const FilterIcon = () => (
  <svg width="20" height="20" viewBox="0 0 20 20" fill="none">
    <path
      d="M2.5 4.16667H17.5L11.6667 11.25V15.8333L8.33333 17.5V11.25L2.5 4.16667Z"
      stroke="white"
      strokeWidth="1.5"
      strokeLinecap="round"
      strokeLinejoin="round"
    />
  </svg>
)

const SEARCH_DEBOUNCE_MS = 250
const MAX_SEARCH_RESULTS = 20
const STATUS_FILTERS = ["open", "scored", "guests"]

const SearchFiltersDropdown = () => {
  const { t } = useTranslation()
  const connector = t("common:topbar.connector")
  const [popoverActive, setPopoverActive] = useState(false)
  const nodeEntity = useEntity(NodeEntity)

  const {
    pendingFilters,
    updatePendingFilter,
    applyFilters,
    setOnlyTreeFilter,
    setOnlyTreeNodeFilter,
    resetTreeFilters,
    hasActiveFilters,
  } = useAppContext()

  const [searchInput, setSearchInput] = useState("")
  const [searchResults, setSearchResults] = useState([])
  const selectedNodeIds = pendingFilters.selectedNodeIds || []

  const nodeEntityRef = useRef(nodeEntity)
  nodeEntityRef.current = nodeEntity

  useEffect(() => {
    const term = searchInput.trim()
    if (!term) {
      setSearchResults((prev) => (prev.length === 0 ? prev : []))
      return undefined
    }

    const handle = setTimeout(async () => {
      try {
        const { data } = await nodeEntityRef.current.search({ value: term })
        const combined = [...(data?.nodes || []), ...(data?.roots || [])]
        const dedup = Object.values(
          combined.reduce((acc, n) => ({ ...acc, [n.id]: n }), {})
        )
        setSearchResults(dedup.slice(0, MAX_SEARCH_RESULTS))
      } catch {
        setSearchResults([])
      }
    }, SEARCH_DEBOUNCE_MS)

    return () => clearTimeout(handle)
  }, [searchInput])

  const togglePopover = () => setPopoverActive((prev) => !prev)

  const handleSearchInputChange = (value) => {
    setSearchInput(value)
  }

  const handleNodeToggle = useCallback(
    (nodeId, checked) => {
      const current = pendingFilters.selectedNodeIds || []
      const next = checked
        ? [...new Set([...current, nodeId])]
        : current.filter((id) => id !== nodeId)
      updatePendingFilter("selectedNodeIds", next)
    },
    [pendingFilters.selectedNodeIds, updatePendingFilter]
  )

  const handleStatusToggle = (key, value) => {
    if (value && key === "open") {
      updatePendingFilter("scored", false)
    } else if (value && key === "scored") {
      updatePendingFilter("open", false)
    }
    updatePendingFilter(key, value)
  }

  const handleOnlyClick = (e, key) => {
    e.stopPropagation()
    setOnlyTreeFilter(key, "")
    setPopoverActive(false)
  }

  const handleOnlyNodeClick = (e, nodeId) => {
    e.stopPropagation()
    setOnlyTreeNodeFilter(nodeId)
    setPopoverActive(false)
  }

  const handleReset = () => {
    resetTreeFilters()
    setSearchInput("")
    setSearchResults([])
    setPopoverActive(false)
  }

  const activator = (
    <div style={{ position: "relative" }}>
      <Button onClick={togglePopover} icon={FilterIcon} plain />
    </div>
  )

  useEffect(() => {
    applyFilters()
  }, [
    pendingFilters.open,
    pendingFilters.scored,
    pendingFilters.guests,
    pendingFilters.selectedNodeIds,
    applyFilters,
  ])

  const buildBreadcrumb = (node) => {
    if (!node?.breadcrumb?.length) return null
    return node.breadcrumb
      .map(NodeOrganization.getNodeNameWithVersion)
      .filter(Boolean)
      .join(connector)
  }

  const renderResultRow = (node) => {
    const name = NodeOrganization.getNodeNameWithVersion(node) || node.name
    const path = buildBreadcrumb(node)

    return (
      <Checkbox
        key={node.id}
        label={
          <span style={{ display: "inline-flex", flexDirection: "column" }}>
            <span>
              <span style={{ fontWeight: 600 }}>{name}</span>
              <span
                role="button"
                tabIndex={0}
                onClick={(e) => handleOnlyNodeClick(e, node.id)}
                onKeyDown={(e) => {
                  if (e.key === "Enter" || e.key === " ") {
                    e.preventDefault()
                    handleOnlyNodeClick(e, node.id)
                  }
                }}
                style={{
                  marginLeft: "8px",
                  color: "#2C6ECB",
                  cursor: "pointer",
                  fontSize: "12px",
                }}
                onMouseEnter={(e) =>
                  (e.currentTarget.style.textDecoration = "underline")
                }
                onMouseLeave={(e) =>
                  (e.currentTarget.style.textDecoration = "none")
                }
              >
                {t("dashboard:chart.filters.only")}
              </span>
            </span>
            {path && (
              <span style={{ fontSize: "12px", color: "#6d7175", fontStyle: "italic" }}>
                {path}
              </span>
            )}
          </span>
        }
        checked={selectedNodeIds.includes(node.id)}
        onChange={(checked) => handleNodeToggle(node.id, checked)}
      />
    )
  }

  return (
    <Popover
      active={popoverActive}
      activator={activator}
      onClose={togglePopover}
      sectioned
      preferredAlignment="right"
    >
      <Stack vertical spacing="tight">
        <TextField
          label={t("common:topbar.search")}
          value={searchInput}
          onChange={handleSearchInputChange}
          placeholder={t("common:topbar.search")}
          clearButton
          onClearButtonClick={() => {
            setSearchInput("")
            setSearchResults([])
          }}
          autoFocus
        />

        {searchResults.length > 0 && (
          <div className="searchFiltersDropdown__results">
            <Stack vertical spacing="extraTight">
              {searchResults.map(renderResultRow)}
            </Stack>
          </div>
        )}

        <Stack vertical spacing="extraTight">
          {STATUS_FILTERS.map((key) => (
            <Checkbox
              key={key}
              label={
                <span>
                  {t(`dashboard:chart.filters.${key}`)}
                  <span
                    role="button"
                    tabIndex={0}
                    onClick={(e) => handleOnlyClick(e, key)}
                    onKeyDown={(e) => {
                      if (e.key === "Enter" || e.key === " ") {
                        e.preventDefault()
                        handleOnlyClick(e, key)
                      }
                    }}
                    style={{
                      marginLeft: "8px",
                      color: "#2C6ECB",
                      cursor: "pointer",
                      fontSize: "12px",
                    }}
                    onMouseEnter={(e) =>
                      (e.currentTarget.style.textDecoration = "underline")
                    }
                    onMouseLeave={(e) =>
                      (e.currentTarget.style.textDecoration = "none")
                    }
                  >
                    {t("dashboard:chart.filters.only")}
                  </span>
                </span>
              }
              checked={pendingFilters[key]}
              onChange={(value) => handleStatusToggle(key, value)}
            />
          ))}
        </Stack>

        {hasActiveFilters && (
          <Stack>
            <Button fullWidth onClick={handleReset}>
              {t("dashboard:chart.filters.reset")}
            </Button>
          </Stack>
        )}
      </Stack>
    </Popover>
  )
}

export default SearchFiltersDropdown
